import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class OutletRating extends ModelObjectWithDirtyCheck<OutletRating> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Rating Description
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Allow Orders?
    */
    public allowOrders: boolean = false;
    /**
    * Exclude from the Outlet List Display
    */
    public excludeFromOutletList: boolean = false;
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.allowOrders = jsonSerializedObject.allowOrders;
          this.excludeFromOutletList = jsonSerializedObject.excludeFromOutletList;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.allowOrders = this.allowOrders;
        toRet.excludeFromOutletList = this.excludeFromOutletList;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "OutletRating.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class OutletRatingService extends ServiceBase {

  public async getAllOutletRatings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletRating)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.outletRatings.map((v: any) => new OutletRating(v));;
    return this.makeJsonRequest("outletRating/v1/getAllOutletRatings", req, false, extractor, opts);
  };

  public async getOutletRating(outletRatingId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletRating> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletRatingId:outletRatingId.toString(),
    };
    const extractor = (response:any) => new OutletRating(response.outletRating);;
    return this.makeJsonRequest("outletRating/v1/getOutletRating", req, false, extractor, opts);
  };

  public async getOutletRatings(outletRatingIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletRating)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletRatingIds:outletRatingIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletRatings.map((v: any) => new OutletRating(v));;
    return this.makeJsonRequest("outletRating/v1/getOutletRatings", req, false, extractor, opts);
  };

  public async saveOutletRatings(outletRatings: readonly OutletRating[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletRatings:outletRatings.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("outletRating/v1/saveOutletRatings", req, true, extractor, opts);
  };

  public async setOutletRatingIsHidden(outletRatingId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletRatingId:outletRatingId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("outletRating/v1/setOutletRatingIsHidden", req, true, extractor, opts);
  };
}

export const outletRatingService = new OutletRatingService();